import type { AxiosInstance } from "axios";
import { API_ROUTES } from "@/constants/apiRoutes";
import { api } from "@/services/apiClient";
import { extractList, isRecord, readString } from "@/services/apiResponseHelpers";
import type { ApiRequestOptions } from "@/services/iamApi";
import { PROJECT_STATUSES, type Project, type ProjectStatus } from "@/types/project";

const DEFAULT_PROJECT_ROLE = "owner";

interface ProjectApiRecord {
  uuid?: unknown;
  name?: unknown;
  description?: unknown;
  status?: unknown;
  organization?: unknown;
  created_at?: unknown;
  updated_at?: unknown;
}

/**
 * Lists the caller's readable IAM projects. Project membership is scoped by
 * the IAM token, so the response contains only the projects the current user
 * belongs to.
 */
export async function fetchProjects(
  options: ApiRequestOptions = {},
  apiOverride?: AxiosInstance,
): Promise<Project[]> {
  const client = apiOverride ?? api;
  const { data } = await client.get<unknown>(API_ROUTES.iam.projects, options);
  return normalizeProjectsResponse(data);
}

export async function createProject(
  project: {
    organizationReference: string;
    name: string;
    description: string;
  },
  options: ApiRequestOptions = {},
): Promise<Project | null> {
  const { data } = await api.post<unknown>(
    API_ROUTES.iam.projects,
    {
      organization: toOrganizationReference(project.organizationReference),
      name: project.name,
      description: project.description,
    },
    options,
  );

  return normalizeProject(data);
}

export async function deleteProject(
  projectUuid: string,
  options: ApiRequestOptions = {},
): Promise<void> {
  await api.delete(
    `${API_ROUTES.iam.projects}${encodeURIComponent(projectUuid)}`,
    options,
  );
}

export async function fetchProject(
  projectUuid: string,
  options: ApiRequestOptions = {},
): Promise<Project | null> {
  const { data } = await api.get<unknown>(
    `${API_ROUTES.iam.projects}${encodeURIComponent(projectUuid)}`,
    options,
  );
  return normalizeProject(data);
}

/**
 * Invites a user into the project by their email or name. The IAM
 * `add_user` action resolves the identifier and binds the configured
 * project role.
 */
export async function addUserToProject(
  projectUuid: string,
  userIdentifier: string,
  roleName = DEFAULT_PROJECT_ROLE,
  options: ApiRequestOptions = {},
): Promise<void> {
  await api.post(
    API_ROUTES.iam.addUserToProject(encodeURIComponent(projectUuid)),
    {
      user: userIdentifier,
      role: roleName,
    },
    options,
  );
}

function toOrganizationReference(reference: string): string {
  const normalizedReference = reference.replace(/\/+$/, "");
  const organizationUuid = normalizedReference.slice(
    normalizedReference.lastIndexOf("/") + 1,
  );
  return `/v1/iam/organizations/${organizationUuid}`;
}

function normalizeProjectsResponse(data: unknown): Project[] {
  return extractList(data)
    .map((record) => normalizeProject(record))
    .filter((project): project is Project => project !== null);
}

function normalizeProject(record: unknown): Project | null {
  if (!isRecord(record)) return null;

  const source = record as ProjectApiRecord;
  const uuid = readString(source.uuid);
  if (!uuid) return null;

  return {
    uuid,
    name: readString(source.name) ?? "",
    description: readString(source.description) ?? "",
    status: normalizeStatus(source.status),
    organization: readString(source.organization),
    createdAt: readString(source.created_at),
    updatedAt: readString(source.updated_at),
  };
}

function normalizeStatus(value: unknown): ProjectStatus {
  const status = readString(value);
  if (status && (PROJECT_STATUSES as readonly string[]).includes(status)) {
    return status as ProjectStatus;
  }
  return "NEW";
}